import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridFooter, GridToolbar } from "@mui/x-data-grid";
import {
  Grid,
  Box,
  Card as MuiCard,
  Typography,
  Divider,
  IconButton,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { spacing } from "@mui/system";
import axios from "axios";
import ViewCustomerInvoiceDialog from "../../components/ViewCustomerInvoiceDialog";
import moment from "moment";
import SelectWrapper from "../../components/SelectBoxDataGridCell";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DataGridExport from "../../components/DataGridExport";
import ConfirmDialog from "../../components/ConfirmDialog";
// import { useMsal } from "@azure/msal-react";
import ViewClaimDialog from "../../components/ViewClaimDialog";
import apiUtils from "../../utils/apiUtils";
import InfoIcon from "@mui/icons-material/InfoOutlined";
// import { formatDate } from "date-fns";
import { GlobalContext } from "../../contexts/GlobalContext";
import { useTranslation } from "react-i18next";
import { useDataGridLocaleText } from "../../utils/useDataGridLocaleText";
import {
  getInvoiceListLabelDisplayValue,
  resolveInvoiceStatusKey,
  translateInvoiceStatusKey,
} from "../../utils/invoiceStatusUtils";

const Card = styled(MuiCard)(spacing);

const buildInvoiceLookupMaps = (claimData, customerPreFinanceData, invoiceLabelData = []) => ({
  claimById: new Map(claimData.map((claim) => [claim.claimId, claim])),
  preFinanceById: new Map(customerPreFinanceData.map((item) => [item.preFinanceId, item])),
  invoiceLabelById: new Map(
    invoiceLabelData.map((label) => [
      label.invoiceLabelId ?? label.InvoiceLabelId,
      label,
    ])
  ),
});

const getApiString = (invoice, camelKey, pascalKey) => invoice[camelKey] ?? invoice[pascalKey] ?? "";

const getLabelApiString = (label, camelKey, pascalKey) => label?.[camelKey] ?? label?.[pascalKey] ?? "";

const enrichInvoiceRow = (invoice, maps) => {
  const claimOfInvoice = maps.claimById.get(invoice.claimId);
  const prefinancedFound = invoice.preFinanceId ? maps.preFinanceById.get(invoice.preFinanceId) : undefined;
  const invoiceLabelId = invoice.invoiceLabelId ?? invoice.InvoiceLabelId;
  const invoiceLabel = maps.invoiceLabelById?.get(invoiceLabelId);

  const formattedInvoiceDate = invoice.invoiceDate ? invoice.invoiceDate.split("T")[0] : invoice.invoiceDate;
  const countryCode = getApiString(invoice, "countryIsoCode", "CountryIsoCode");
  const currencyCode = getApiString(invoice, "currencyIsoCode", "CurrencyIsoCode");
  const claimCode = getApiString(invoice, "claimCode", "ClaimCode") || (claimOfInvoice ? claimOfInvoice.claimCode : "");
  const invoiceLabelDesc =
    getLabelApiString(invoiceLabel, "invoiceLabelDescription", "InvoiceLabelDescription") ||
    getApiString(invoice, "invoiceLabelDescription", "InvoiceLabelDescription");
  const invoiceLabelShortCode = getLabelApiString(invoiceLabel, "invoiceLabelShortCode", "InvoiceLabelShortCode");
  const invoiceStatusDesc = resolveInvoiceStatusKey(invoice, claimOfInvoice, !!prefinancedFound);
  const supplierName = getApiString(invoice, "supplierName", "SupplierName");
  const preFinanced = invoice.preFinanced ?? invoice.PreFinanced ?? (prefinancedFound ? 1 : 0);
  const formattedPreFinancedDate =
    getApiString(invoice, "preFinancedDate", "PreFinancedDate") ||
    (prefinancedFound && prefinancedFound.payoutDate && prefinancedFound.payoutDate > "01-01-1970"
      ? new Date(prefinancedFound.payoutDate).toLocaleDateString("nl-NL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "");

  return {
    ...invoice,
    currencyCode,
    countryCode2: countryCode,
    invoiceDate: formattedInvoiceDate,
    claimCode,
    invoiceLabelDesc,
    invoiceLabelShortCode,
    supplierName,
    preFinanced,
    preFinancedDate: formattedPreFinancedDate,
    vatAmount: invoice.invoiceVATAmountLocalCurrency,
    invoiceStatusDesc,
  };
};

const applyInvoiceDateFilters = (invoices, selectedCountry, selectedYear, selectedStartMonth, selectedEndMonth) => {
  let filtered = invoices;

  if (selectedCountry !== "any") {
    filtered = filtered.filter((inv) => inv.countryCode === selectedCountry);
  }
  if (selectedYear !== "any") {
    filtered = filtered.filter((inv) => {
      const invYear = new Date(inv.invoiceDate).getFullYear();
      return invYear === selectedYear;
    });
  }
  if (selectedYear !== "any" && selectedStartMonth !== "any" && selectedEndMonth !== "any") {
    filtered = filtered.filter((inv) => {
      const invYear = new Date(inv.invoiceDate).getFullYear();
      const invMonth = new Date(inv.invoiceDate).getMonth();
      const filterStartDate = new Date(selectedYear, selectedStartMonth - 1, 1);
      const filterEndDate = new Date(selectedYear, selectedEndMonth, 0);
      const invoiceDate = new Date(invYear, invMonth, 1);
      return invoiceDate >= filterStartDate && invoiceDate <= filterEndDate;
    });
  }

  return filtered;
};

const filterInvoicesByTab = (invoices, activeTab, maps) => {
  switch (activeTab) {
    case 0:
      return invoices.filter((invoice) => invoice.invoiceStateId > 1);
    case 1:
      return invoices.filter((invoice) => {
        if (maps.preFinanceById.has(invoice.preFinanceId)) {
          return false;
        }
        const claim = maps.claimById.get(invoice.claimId);
        return claim?.claimStateId === 4;
      });
    case 2:
      return invoices.filter((invoice) => {
        if (maps.preFinanceById.has(invoice.preFinanceId)) {
          return false;
        }
        const claim = maps.claimById.get(invoice.claimId);
        return claim?.claimStateId === 16 && invoice.invoiceStateId !== 9;
      });
    case 3:
      return invoices.filter((invoice) => maps.preFinanceById.has(invoice.preFinanceId));
    case 4:
      return invoices.filter((invoice) => {
        if (maps.preFinanceById.has(invoice.preFinanceId)) {
          return false;
        }
        return invoice.invoiceStateId === 4;
      });
    case 5:
      return invoices.filter((invoice) => {
        if (maps.preFinanceById.has(invoice.preFinanceId)) {
          return false;
        }
        const claim = maps.claimById.get(invoice.claimId);
        return claim?.claimStateId === 16 && invoice.invoiceStateId === 9;
      });
    case 6:
      return invoices.filter((invoice) => {
        if (maps.preFinanceById.has(invoice.preFinanceId)) {
          return false;
        }
        return invoice.invoiceStateId === 2;
      });
    default:
      return invoices;
  }
};

const CustomerInvoices = (props) => {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [pageSize, setPageSize] = useState(15);
  const [activeTab, setActiveTab] = useState(0);
  const [allInvoicesRaw, setAllInvoicesRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const supplierData = apiUtils.useApiData("/api/v1/suppliers");
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const productData = apiUtils.useApiData("/api/v1/productTypes");
  const currencyData = apiUtils.useApiData("/api/v1/currencies");
  const invoiceLabelData = apiUtils.useApiData("/api/v1/invoiceLabels");
  const { customerId, customerNumber } = useContext(GlobalContext);
  const [selectedInvoiceCount, setSelectedInvoiceCount] = useState(0);
  const [selectedInvoiceTotalVATAmount, setSelectedInvoiceTotalVATAmount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const [viewClaimDialog, setViewClaimDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
  });

  var updatedData;

  const [claimData, setClaimData] = useState([]);
  const [customerPreFinanceData, setCustomerPreFinanceData] = useState([]);

  const lookupMaps = useMemo(
    () => buildInvoiceLookupMaps(claimData, customerPreFinanceData, invoiceLabelData),
    [claimData, customerPreFinanceData, invoiceLabelData]
  );

  const fetchInvoices = useCallback(async () => {
    if (!customerId) {
      return;
    }

    setLoading(true);

    try {
      const [invoicesResponse, claimsResponse, preFinanceResponse] = await Promise.all([
        axios.get("/api/v1/customerInvoicesByEntityId/" + customerId),
        axios.get("/api/v1/claimsLookupByEntityId/" + customerId),
        axios.get("/api/v1/preFinanceByEntityId/" + customerId),
      ]);

      setAllInvoicesRaw(invoicesResponse.data);
      setClaimData(claimsResponse.data);
      setCustomerPreFinanceData(preFinanceResponse.data);
    } catch (error) {
      console.error("Error fetching invoice list data:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const [selectedCountry, setSelectedCountry] = useState("any");
  const [selectedYear, setSelectedYear] = useState("any");
  const [selectedStartMonth, setSelectedStartMonth] = useState("any");
  const [selectedEndMonth, setSelectedEndMonth] = useState("any");

  const displayInvoices = useMemo(() => {
    if (allInvoicesRaw.length === 0) {
      return [];
    }

    const enrichedInvoices = allInvoicesRaw.map((invoice) => enrichInvoiceRow(invoice, lookupMaps));
    const dateFiltered = applyInvoiceDateFilters(
      enrichedInvoices,
      selectedCountry,
      selectedYear,
      selectedStartMonth,
      selectedEndMonth
    );

    return filterInvoicesByTab(dateFiltered, activeTab, lookupMaps);
  }, [
    allInvoicesRaw,
    lookupMaps,
    selectedCountry,
    selectedYear,
    selectedStartMonth,
    selectedEndMonth,
    activeTab,
  ]);

  const handleFile = (updatedData) => {};

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {}, // Empty function for now, will be updated when needed
  });

  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
  });

  function onEditConfirmed(e, params) {
    window.location.reload(false);
    setViewCustomerInvoiceDialog({
      ...viewCustomerInvoiceDialog,
      isOpen: false,
    });
  }

  const handleEditCellChange = (params) => {
    setAllInvoicesRaw((prev) =>
      prev.map((row) =>
        row.customerInvoiceId === params.id ? { ...row, [params.field]: params.props.value } : row
      )
    );
  };

  function generateArrayOfYears() {
    var max = new Date().getFullYear() + 1;
    var min = max - 5;
    var years = [];
    for (var i = max; i >= min; i--) {
      years.push({ yearId: i, yearText: i });
    }

    return years;
  }

  let yearArr = generateArrayOfYears().reverse();

  const monthArr = [
    { monthId: 1, monthText: t("Jan") },
    { monthId: 2, monthText: t("Feb") },
    { monthId: 3, monthText: t("Mar") },
    { monthId: 4, monthText: t("Apr") },
    { monthId: 5, monthText: t("May") },
    { monthId: 6, monthText: t("Jun") },
    { monthId: 7, monthText: t("Jul") },
    { monthId: 8, monthText: t("Aug") },
    { monthId: 9, monthText: t("Sep") },
    { monthId: 10, monthText: t("Oct") },
    { monthId: 11, monthText: t("Nov") },
    { monthId: 12, monthText: t("Dec") },
  ];

  const handleCountryValue = (value, name) => {
    setSelectedCountry(value);
  };
  const handleYearValue = (value, name) => {
    setSelectedYear(value);
  };
  const handleStartMonthValue = (value, name) => {
    setSelectedStartMonth(value);
  };
  const handleEndMonthValue = (value, name) => {
    setSelectedEndMonth(value);
  };

  const handleResetFilter = () => {
    setSelectedCountry("any");
    setSelectedYear("any");
    setSelectedStartMonth("any");
    setSelectedEndMonth("any");
  };

  const BulkEditFooter = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #ccc",
          padding: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total Selected Invoices")}:</div>
            <div style={{ marginBottom: 5 }}>{selectedInvoiceCount}</div>
          </div>
          {/* <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>Total Gross Amount:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalAmount.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div> */}
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total VAT Amount")}:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalVATAmount.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          {/* <div
            style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}
          >
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>
              Total VAT Amount EUR:
            </div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalVATAmountEUR.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div> */}
        </div>
        <div>
          <div>
            <GridFooter
              style={{ borderTop: "none" }}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[15, 50, 100]}
            />
          </div>
        </div>
      </div>
    );
  };

  const fetchClaimsByEntityId = () => {
    fetchInvoices();
  };

  // Utility function to format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const columns = [
    // {
    //   field: "customerInvoiceId",
    //   headerName: "id",
    //   flex: 0.5,
    //   hide: true,
    // },
    {
      field: "invoiceNumber",
      headerName: t("Invoice Number"),
      flex: 0.7,
    },
    {
      field: "claimCode",
      headerName: t("Claim Code"),
      flex: 0.65,
      renderCell: (params) => {
        const { invoiceStateId, invoiceVATAmountLocalCurrency, claimId } = params.row;
        let invoiceClaimData = lookupMaps.claimById.get(claimId);
        // console.log("invoiceClaimData", params.row);
        if (
          invoiceStateId === 4 ||
          invoiceVATAmountLocalCurrency === 0 ||
          invoiceClaimData?.claimStateId === 1 ||
          invoiceClaimData?.claimStateId === 3
        ) {
          return ""; // Return an empty string when conditions are met
        }

        return (
          <Button
            variant="text"
            onClick={() => {
              setViewClaimDialog({
                ...viewClaimDialog,
                isOpen: true,
                title: t("View Claim"),
                params: params.row, // Pass the row data to the dialog
                custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
              });
            }}
          >
            {params.value}
          </Button>
        );
      },
    },

    {
      field: "invoiceDate",
      headerName: t("Invoice Date"),
      flex: 0.45,
      valueFormatter: (params) => moment(params?.value).format("DD-MM-YYYY"),
    },
    {
      field: "invoiceStatusDesc",
      headerName: t("Invoice State"),
      flex: 0.7,
      valueFormatter: (params) => translateInvoiceStatusKey(params.value, t),
    },
    {
      field: "invoiceLabelDesc",
      headerName: t("Invoice Label"),
      flex: 0.8,
      // MUI Data Grid v5 valueFormatter does not receive row — use renderCell for translation.
      renderCell: (params) => getInvoiceListLabelDisplayValue(params.row, t),
      valueFormatter: (params) => getInvoiceListLabelDisplayValue(null, t, params?.value),
    },
    {
      field: "supplierName",
      headerName: t("Supplier"),
      flex: 1,
    },
    {
      field: "countryCode2",
      headerName: t("Country"),
      flex: 0.3,
    },
    {
      field: "currencyCode",
      headerName: t("Currency"),
      flex: 0.35,
    },
    {
      field: "vatAmount",
      headerName: t("VAT Amount"),
      flex: 0.45,
      align: "right",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: "15px",
          }}
        >
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "preFinanced",
      headerName: t("Pre-Financed"),
      flex: 0.45,
      renderCell: (params) => (params.value > 0 ? <CheckCircleOutlineIcon style={{ color: "green", margin: "0 auto" }} /> : ""),
    },
    {
      field: "preFinancedDateSortable",
      headerName: t("Payout Date"),
      width: 100,
      sortingOrder: ["asc", "desc"],
      valueGetter: (params) => {
        const nonRealDates = [
          "0000-00-00",
          "01-01-1900",
          "01-01-1970",
          "00-00-0000",
          "01-01-0001",
          "0001-01-01",
          "01-01-1",
          "0001-01-01T00:00:00",
          "1-1-1",
          "01-1-1",
          "1-01-1",
        ];
        const { invoiceStateId, invoiceVATAmountLocalCurrency, claimId, preFinanceId } = params.row;
        let invoiceClaimData = lookupMaps.claimById.get(claimId);
        if (
          invoiceStateId === 4 ||
          invoiceVATAmountLocalCurrency === 0 ||
          invoiceClaimData?.claimStateId === 1 ||
          invoiceClaimData?.claimStateId === 3
        ) {
          return Number.MAX_SAFE_INTEGER;
        }
        if (invoiceClaimData) {
          const rowData = params.row;
          const lastTwoDigits = invoiceClaimData.claimCode.slice(-2);
          if (lastTwoDigits === "11") {
            if (rowData?.preFinanceId > 0) {
              let preFinanceData = lookupMaps.preFinanceById.get(rowData.preFinanceId);
              if (preFinanceData && preFinanceData.payoutDate) {
                const ts = new Date(preFinanceData.payoutDate).getTime();
                return isNaN(ts) || nonRealDates.includes(preFinanceData.payoutDate) ? Number.MAX_SAFE_INTEGER : ts;
              }
              return Number.MAX_SAFE_INTEGER;
            } else {
              return Number.MAX_SAFE_INTEGER;
            }
          }
          if (lastTwoDigits === "01") {
            if (nonRealDates.includes(invoiceClaimData.payDate)) {
              return Number.MAX_SAFE_INTEGER;
            } else {
              const ts = new Date(invoiceClaimData.payDate).getTime();
              return isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts;
            }
          }
          if (lastTwoDigits === "10") {
            return Number.MAX_SAFE_INTEGER;
          }
          if (lastTwoDigits === "00") {
            if (invoiceClaimData?.claimStateId === 4 && preFinanceId === 0) {
              return Number.MAX_SAFE_INTEGER;
            }
            const ts = new Date(invoiceClaimData.reimbursementDate).getTime();
            return isNaN(ts) || nonRealDates.includes(invoiceClaimData.reimbursementDate) ? Number.MAX_SAFE_INTEGER : ts;
          }
        }
        return Number.MAX_SAFE_INTEGER;
      },
      sortComparator: (a, b) => a - b,
      renderCell: (params) => {
        const nonRealDates = [
          "0000-00-00",
          "01-01-1900",
          "01-01-1970",
          "00-00-0000",
          "01-01-0001",
          "0001-01-01",
          "01-01-1",
          "0001-01-01T00:00:00",
          "1-1-1",
          "01-1-1",
          "1-01-1",
        ];
        const { invoiceStateId, invoiceVATAmountLocalCurrency, claimId, preFinanceId } = params.row;
        let invoiceClaimData = lookupMaps.claimById.get(claimId);
        if (
          invoiceStateId === 4 ||
          invoiceVATAmountLocalCurrency === 0 ||
          invoiceClaimData?.claimStateId === 1 ||
          invoiceClaimData?.claimStateId === 3
        ) {
          return "";
        }
        if (invoiceClaimData) {
          const rowData = params.row;
          const lastTwoDigits = invoiceClaimData.claimCode.slice(-2);
        if (lastTwoDigits === "11") {
            if (rowData?.preFinanceId > 0) {
              let preFinanceData = lookupMaps.preFinanceById.get(rowData.preFinanceId);
              if (preFinanceData && preFinanceData.payoutDate) {
              return nonRealDates.includes(preFinanceData.payoutDate) ? "" : formatDate(preFinanceData.payoutDate);
              }
            return t("N/A");
            } else {
            return t("N/A");
            }
          }
          if (lastTwoDigits === "01") {
            if (nonRealDates.includes(invoiceClaimData.payDate)) {
              return "";
            } else {
              return formatDate(invoiceClaimData.payDate);
            }
          }
          if (lastTwoDigits === "10") {
            return t("N/A");
          }
          if (lastTwoDigits === "00") {
            if (invoiceClaimData?.claimStateId === 4 && preFinanceId === 0) {
              return "";
            }
            if (nonRealDates.includes(invoiceClaimData.reimbursementDate)) {
              return t("N/A");
            }
            const formattedDate = formatDate(invoiceClaimData.reimbursementDate);
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                {formattedDate}
                <Tooltip
                  title={t("Date of decision on claim, pay-out may differ a few days/weeks.")}
                  enterTouchDelay={0}
                  placement="top"
                >
                  <InfoIcon sx={{ color: "#376FD0", marginLeft: 1, fontSize: 16, cursor: "pointer" }} />
                </Tooltip>
              </div>
            );
          }
        } else {
          return "";
        }
      },
    },
    // {
    //   field: "fileId",
    //   headerName: "PDF Available",
    //   flex: 0.4,
    //   renderCell: (params) =>
    //     params.value > 0 ? (
    //       <CheckCircleOutlineIcon
    //         style={{ color: "green", margin: "0 auto" }}
    //       />
    //     ) : (
    //       <CheckCircleOutlineIcon style={{ color: "red", margin: "0 auto" }} />
    //     ),
    // },
    {
      field: "actions",
      headerName: t("View"),
      flex: 0.3,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              onClick={(e) => {
                setViewCustomerInvoiceDialog({
                  isOpen: true,
                  downloadFile: true,
                  title: t("View Invoice"),
                  params: params.row,
                  custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                  subTitle: t("Are you sure you want to manage {{name}}?", { name: params.row.contactName }),
                  onConfirm: () => {
                    onEditConfirmed(e, params.row);
                  },
                  handleFile: () => {
                    handleFile(updatedData);
                  },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
    // let selectedRowsAmount = 0;
    let selectedRowsVATAmount = 0;
    // let disablePreFinanced = false;

    newSelection.forEach((rowId) => {
      const selectedRow = displayInvoices.find((row) => row.customerInvoiceId === rowId);
      if (selectedRow) {
        // selectedRowsAmount += selectedRow.invoiceGrossAmount;
        selectedRowsVATAmount += selectedRow.invoiceVATAmountLocalCurrency;

        // Check if preFinancedId is greater than 0
        // if (selectedRow.preFinanceId > 0) {
        //   disablePreFinanced = true;
        // }
      }
    });

    setSelectedInvoiceCount(newSelection.length);
    // setSelectedInvoiceTotalAmount(selectedRowsAmount);
    setSelectedInvoiceTotalVATAmount(selectedRowsVATAmount);

    // Update the button's disabled state
    // setIsPreFinancedDisabled(disablePreFinanced);
  };

  return (
    <React.Fragment>
      <Helmet title={t("Customer Invoices")} />
      <ViewClaimDialog
        viewClaimDialog={viewClaimDialog}
        setViewClaimDialog={setViewClaimDialog}
        refreshTable={fetchClaimsByEntityId}
      />
      <Tabs value={activeTab} onChange={handleTabChange} aria-label={t("Invoice Tabs")} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tab label={t("All Invoices")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Pending Invoices")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Reimbursed Invoices")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Pre-Financed Invoices")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Not Claimable Invoices")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Rejected Invoices")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("To Next Claim")} sx={{ typography: "h6", fontWeight: "bold" }} />
      </Tabs>
      <Card mb={6}>
        <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />

        <Divider />
        <Grid container spacing={2} mt={0}>
          <Grid container xs={12} spacing={3} sx={{ pr: 2, mt: 2, ml: 2, mb: -2 }}>
            <Grid item xs={2}>
              <SelectWrapper
                name="countryId"
                labelColor="#f64435"
                label={t("Country")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(
                      JSON.parse(
                        JSON.stringify(countryData.filter((country) => Number(country.countryId) < 50)).split('"countryId":').join('"key":')
                      )
                    )
                      .split('"countryCode":')
                      .join('"value":')
                  ),
                ]}
                value={selectedCountry}
                handleChange={handleCountryValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="year"
                label={t("Year")}
                // value={year}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(yearArr).split('"yearId":').join('"key":')))
                      .split('"yearText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedYear}
                handleChange={handleYearValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="startMonth"
                label={t("Start month")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedStartMonth}
                handleChange={handleStartMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="endMonth"
                label={t("End month")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedEndMonth}
                handleChange={handleEndMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              {/* <Button
                onClick={handleFilter}
                sx={{ mt: 4, pl: 6, pr: 6 }}
                variant="contained"
              >
                Filter
              </Button> */}
              <Button onClick={handleResetFilter} sx={{ mt: 4, ml: 4, pl: 6, pr: 6 }} variant="outlined">
                {t("Reset")}
              </Button>
            </Grid>
          </Grid>
          <ViewCustomerInvoiceDialog
            viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
            setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
            countryArr={countryData}
            supplierArr={supplierData}
            currencyArr={currencyData}
            productTypeArr={productData}
            refreshGrid={fetchInvoices}
          />
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <DataGridExport
                tableData={displayInvoices}
                columns={columns}
                exportType="invoices"
                claimData={claimData}
                customerPreFinanceData={customerPreFinanceData}
              />
            </div>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "600px",
                }}
              >
                <Typography sx={{ mr: 6, fontSize: 20, color: "text.primary" }}>{t("Loading")}</Typography>
                <CircularProgress />
              </div>
            ) : (
              <DataGrid
                // isRowSelectable={(params) => params.row.preFinanceId <= 0 || params.row.preFinanceId == null}
                checkboxSelection
                idProperty="id"
                editMode="cell"
                getRowId={(row) => row.customerInvoiceId}
                localeText={dataGridLocaleText}
                onEditCellChange={handleEditCellChange}
                selectionModel={selectedRows}
                onSelectionModelChange={handleSelectionChange}
                components={{ Toolbar: GridToolbar, Footer: BulkEditFooter }}
                density="compact"
                disableColumnSelector
                componentsProps={{
                  toolbar: {
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                  },
                }}
                rowIndexColumn
                experimentalFeatures={{ newEditingApi: true }}
                rows={displayInvoices}
                columns={columns}
                pagination
                paginationMode="client"
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[15, 50, 100]}
                sx={{
                  height: 677,
                  width: "100%",
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 2,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  "& .MuiTablePagination-root": {
                    color: "text.primary",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    color: "text.primary",
                  },
                  "& .MuiTablePagination-displayedRows": {
                    color: "text.primary",
                  },
                  "& .MuiTablePagination-select": {
                    color: "text.primary",
                  },
                  "& .MuiTablePagination-selectIcon": {
                    color: "text.primary",
                  },
                  "& .MuiTablePagination-actions button": {
                    color: "text.primary",
                  },
                  marginTop: 0,
                }}
              />
            )}
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default CustomerInvoices;
